import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Sparkles, Palette, ShieldCheck, Smartphone, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR } from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export const REF_KEY = "miltroo_ref_code";

export function StartRedirect() {
  const code = localStorage.getItem(REF_KEY);
  if (code) return <Navigate to={`/start/${code}`} replace />;
  return (
    <div data-testid="start-invalid" className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md w-full text-center space-y-4 rounded-2xl border border-border bg-card p-10">
        <p className="font-display font-bold text-lg">Referral link missing</p>
        <p className="text-sm text-muted-foreground">Please use the complete referral link shared by your MILTROO partner.</p>
        <Link to="/" data-testid="start-go-home" className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity">
          Go to Home
        </Link>
      </div>
    </div>
  );
}

const TYPES = [
  { v: "ecommerce", l: "Online Store / E-Commerce" },
  { v: "restaurant", l: "Restaurant / Food" },
  { v: "services", l: "Services / Local Business" },
  { v: "portfolio", l: "Portfolio / Personal Brand" },
  { v: "business", l: "Business / Corporate" },
  { v: "other", l: "Other" },
];

const PERKS = [
  { icon: Palette, t: "180+ premium themes", d: "Pick a design that matches your business — customize everything." },
  { icon: Smartphone, t: "Mobile app ready", d: "Every website is a PWA — your customers can install it like an app." },
  { icon: ShieldCheck, t: "Secure & managed", d: "Hosting, SSL and updates handled for you. No technical work needed." },
];

function Field({ label, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label className="overline-label">{label}</Label>
      <Input {...props} className="h-12 rounded-md bg-background" />
    </div>
  );
}

export default function ReferralLanding() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", business_name: "", email: "", phone: "", password: "", business_type: "ecommerce" });

  useEffect(() => {
    if (!code) return;
    localStorage.setItem(REF_KEY, code);
    api.get(`/public/r/${code}`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e)));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/public/r/${code}/signup`, form);
      localStorage.setItem("zylo_token", data.token);
      toast.success("Account created — let's build your website");
      window.location.href = "/self-service/setup";
    } catch (e2) {
      toast.error(errMsg(e2));
      setBusy(false);
    }
  };

  if (err) {
    return (
      <div data-testid="referral-invalid" className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md w-full text-center space-y-4 rounded-2xl border border-border bg-card p-10">
          <p className="font-display font-bold text-lg">This referral link is not valid</p>
          <p className="text-sm text-muted-foreground">{err}</p>
          <Link to="/" data-testid="referral-go-home" className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-12 w-12 rounded-xl bg-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div data-testid="referral-landing-page" className="min-h-screen bg-background">
      <header className="border-b border-border/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandLogo size="md" />
          <Link to="/business-owner/login" data-testid="referral-login-link" className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
            Already registered? Login
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12 lg:py-20 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div data-testid="referral-partner-badge" className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-2 text-xs font-bold mb-6">
            <Sparkles className="h-3.5 w-3.5" />
            {info.partner?.name ? `Special offer via ${info.partner.name}` : "Special partner offer"}
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
            Launch your business website <span className="text-gradient">today.</span>
          </h1>
          <p className="text-muted-foreground mt-4 text-base sm:text-lg max-w-lg leading-relaxed">
            Create your account, pick a premium theme, and go live — online store, restaurant, services or portfolio.
          </p>

          <div data-testid="referral-price-card" className="mt-8 rounded-2xl border border-border bg-card p-6 flex items-center gap-5">
            <div>
              <p className="overline-label">One-time setup</p>
              <p className="font-display font-extrabold text-4xl tracking-tight mt-1" data-testid="referral-price">
                {fmtINR(info.price)}
              </p>
              {info.offer_active && <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 mt-1">Limited-time offer price</p>}
            </div>
            <div className="h-14 w-px bg-border" />
            <ul className="text-sm text-muted-foreground space-y-1.5">
              {["Full website + dashboard", "1 year access included", "Manual payment verification"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />{t}</li>
              ))}
            </ul>
          </div>

          <div className="mt-8 space-y-4">
            {PERKS.map((p, i) => (
              <motion.div key={p.t} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.08 }} className="flex gap-4">
                <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
                  <p.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-sm">{p.t}</p>
                  <p className="text-sm text-muted-foreground">{p.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          <form data-testid="referral-signup-form" onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-4 shadow-xl shadow-black/5">
            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight">Create your account</h2>
              <p className="text-sm text-muted-foreground mt-1">Takes less than a minute. Your website builder unlocks right after.</p>
            </div>
            <Field label="Your Full Name" data-testid="ref-signup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Anil Kumar" />
            <Field label="Business / Store Name" data-testid="ref-signup-business" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="e.g. Anil Fashion Store" />
            <div className="space-y-1.5">
              <Label className="overline-label">Business Type</Label>
              <select data-testid="ref-signup-type" value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm">
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            <Field label="Email" data-testid="ref-signup-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
            <Field label="Mobile Number (optional)" data-testid="ref-signup-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" />
            <Field label="Password" data-testid="ref-signup-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 6 characters" />
            <Button data-testid="ref-signup-submit" type="submit" disabled={busy}
              className="w-full h-12 rounded-md font-bold text-base active:scale-[0.98] transition-transform">
              {busy ? "Creating account..." : <>Start Building My Website <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">By continuing you agree to the MILTROO terms. Payment happens after you pick your theme.</p>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
